import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageSquare } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, Spoiler, Empty } from "@/components/Common";
import { Stars } from "@/components/StarInput";
import { ReportButton } from "@/components/ReportButton";

export function LikeButton({ type, item }) {
  const { user } = useAuth();
  const [s, setS] = useState({ on: item.reacted, n: item.reactions || 0 });
  const go = async () => {
    if (!user) return toast("Sign in to react");
    try { const { data } = await api.post("/reactions", { target_type: type, target_id: item.id }); setS({ on: data.reacted, n: data.count }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return <button onClick={go} data-testid={`like-${type}-${item.id}`} className={`inline-flex items-center gap-1 text-xs ${s.on ? "text-pink-400" : "text-slate-500 hover:text-pink-300"}`}><Heart className={`h-3.5 w-3.5 ${s.on ? "fill-pink-400" : ""}`} />{s.n}</button>;
}

function ReviewForm({ title, existing, onSaved }) {
  const [f, setF] = useState({ headline: existing?.headline || "", body: existing?.body || "", spoiler: existing?.spoiler || false });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/titles/${title.id}/reviews`, f); toast.success(existing ? "Review updated" : "Review published"); onSaved(data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-3 rounded-2xl border border-white/5 bg-[#12121A] p-5" data-testid="review-form">
      <input data-testid="review-headline" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} maxLength={120} placeholder="Headline (optional)"
        className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-amber-400" />
      <textarea data-testid="review-body" required minLength={10} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} maxLength={5000} placeholder="What did you think? (min 10 characters)"
        className="min-h-[120px] w-full rounded-xl border border-white/10 bg-black/30 p-4 text-sm outline-none focus:border-amber-400" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-400"><input data-testid="review-spoiler" type="checkbox" checked={f.spoiler} onChange={(e) => setF({ ...f, spoiler: e.target.checked })} className="accent-amber-400" />Contains spoilers</label>
        <button data-testid="review-submit" disabled={busy} className="rounded-full bg-amber-400 px-6 py-2.5 text-sm font-bold text-black hover:bg-amber-300 disabled:opacity-60">{existing ? "Update review" : "Publish review"}</button>
      </div>
    </form>
  );
}

export function Reviews({ title, mine, onMine }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [writing, setWriting] = useState(false);
  const load = useCallback(() => api.get(`/titles/${title.id}/reviews`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, error: true })), [title.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <section id="reviews" data-testid="reviews-section" className="scroll-mt-24">
      <div className="mb-4 flex items-end justify-between">
        <h2 className="font-display text-4xl">Member Reviews <span className="text-xl text-slate-500">{data?.total || 0}</span></h2>
        {user ? <button data-testid="write-review-btn" onClick={() => setWriting((w) => !w)} className="rounded-full border border-amber-400/40 px-4 py-2 text-sm text-amber-300 hover:bg-amber-400/10">{mine ? "Edit your review" : "Write a Review"}</button>
          : <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} data-testid="write-review-signin" className="text-sm font-semibold text-amber-400">Sign in to review</Link>}
      </div>
      {writing && <div className="mb-6"><ReviewForm title={title} existing={mine} onSaved={(r) => { onMine(r); setWriting(false); load(); }} /></div>}
      {data?.error && <p className="text-sm text-red-400">Reviews couldn't load right now.</p>}
      {data && !data.items.length && !data.error && <Empty title="No reviews yet" text="Be the first to share a genuine opinion on this title." testid="reviews-empty" />}
      <div className="space-y-4">
        {data?.items.map((r) => (
          <article key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl border border-white/5 bg-[#12121A] p-5">
            <div className="flex items-center gap-3">
              <Link to={`/u/${r.user.username}`}><Avatar user={r.user} size={34} /></Link>
              <div className="flex-1">
                <Link to={`/u/${r.user.username}`} className="text-sm font-semibold hover:text-amber-400">@{r.user.username}</Link>
                <p className="text-xs text-slate-500">{fmtDate(r.created_at)}</p>
              </div>
              {r.user_rating && <Stars value={r.user_rating} size={13} />}
            </div>
            <Spoiler spoiler={r.spoiler} id={r.id}>
              {r.headline && <p className="mt-3 font-semibold">{r.headline}</p>}
              <p className="mt-2 whitespace-pre-line text-sm text-slate-300">{r.body}</p>
            </Spoiler>
            <div className="mt-3 flex gap-4"><LikeButton type="review" item={r} /><ReportButton type="review" id={r.id} /></div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function Discussion({ title }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ kind: "theory", title: "", body: "", spoiler: false });
  const load = useCallback(() => api.get("/community/feed", { params: { title_id: title.id } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), [title.id]);
  useEffect(() => { load(); }, [load]);
  const post = async (e) => {
    e.preventDefault();
    try { await api.post("/community/posts", { ...f, title_id: title.id }); toast.success("Discussion started"); setF({ ...f, title: "", body: "" }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section id="discussion" data-testid="discussion-section" className="scroll-mt-24">
      <h2 className="mb-4 font-display text-4xl">Discussion</h2>
      {user ? (
        <form onSubmit={post} className="mb-5 space-y-3 rounded-2xl border border-white/5 bg-[#12121A] p-5" data-testid="discussion-form">
          <div className="flex flex-wrap gap-2">
            {["theory", "question", "discussion", "recommendation"].map((k) => <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} className={`rounded-full border px-3 py-1 text-xs capitalize ${f.kind === k ? "border-purple-400 bg-purple-500/15 text-purple-200" : "border-white/10 text-slate-400"}`}>{k}</button>)}
          </div>
          <input data-testid="discussion-title" required minLength={3} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Your theory, question or take" className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-purple-400" />
          <textarea data-testid="discussion-body" required minLength={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Explain…" className="min-h-[90px] w-full rounded-xl border border-white/10 bg-black/30 p-4 text-sm outline-none focus:border-purple-400" />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-slate-400"><input data-testid="discussion-spoiler" type="checkbox" checked={f.spoiler} onChange={(e) => setF({ ...f, spoiler: e.target.checked })} className="accent-amber-400" />Contains spoilers</label>
            <button data-testid="discussion-submit" className="rounded-full bg-purple-600 px-5 py-2 text-sm font-bold hover:bg-purple-500">Post</button>
          </div>
        </form>
      ) : <p className="mb-4 text-sm text-slate-400"><Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="font-semibold text-purple-300" data-testid="join-discussion-signin">Sign in</Link> to join the discussion.</p>}
      {items && !items.length && <p className="rounded-xl border border-dashed border-white/10 p-5 text-sm text-slate-500">No discussions yet. Start the first theory!</p>}
      <div className="space-y-3">
        {items?.map((p) => (
          <Link key={p.id} to={`/community/post/${p.id}`} data-testid={`discussion-${p.id}`} className="block rounded-2xl border border-white/5 bg-[#12121A] p-4 hover:border-purple-400/30">
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300">{p.kind}{p.spoiler ? " · spoiler" : ""}</span>
            <p className="font-semibold">{p.title}</p>
            <p className="mt-1 flex items-center gap-3 text-xs text-slate-500">@{p.user.username}<span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{p.comments}</span></p>
          </Link>
        ))}
      </div>
    </section>
  );
}
